import {
  Button,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  NativeSelect,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  handleCreateAccountApi,
  handleDeleteAccountApi,
  handleGetAccountsApi,
  handleGetDeactivatedAccountsApi,
  handleUpdateAccountApi,
  reactivateAccount,
} from "../../../api/mosaic/accounts";
import { useAppDispatch, useAppSelector } from "../../../store/reduxHooks";
import { handleGetCompanies } from "../../../api/mosaic";
import { setCompanyData } from "../../../store/mosaic/companySlice";
import {
  EFormAction,
  IAddAccountRequest,
  UserResponse,
} from "../../../api/mosaic/interface";
import UserModal from "./UserModal";
import ReactivateUserModal from "./ReactivateUserModal";
import { useConfirmDialog } from "../../shared/ConfirmDialogProvider";
import { useToast } from "../../shared/Toast";
import SearchBar from "../../shared/SearchBar";
import SortableTableCell from "../../shared/SortableTableCell";
import { useSortable } from "../../shared/useSortable";
import { editButtonSx, deleteButtonSx, addButtonSx } from "../../shared/buttonStyles";
import PageHeader from "../../shared/PageHeader";
import PageCard from "../../shared/PageCard";
import { emailTemplate } from "../../../utils/constants";

const defaultFormData = {
  action: EFormAction.CREATE,
  firstName: "",
  lastName: "",
  email: "",
  companyId: 0,
  roleId: 0,
  departmentId: null,
  templateId: emailTemplate.addAccount,
};

const UserComponent: React.FC = () => {
  const [openModal, setOpenModal] = React.useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const user = useAppSelector((state) => state.userSlice.user);
  const dispatch = useAppDispatch();
  const { confirm } = useConfirmDialog();
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formAction, setFormAction] = useState<EFormAction>(EFormAction.CREATE);
  const [formData, setFormData] = useState<IAddAccountRequest>(
    {} as IAddAccountRequest
  );
  const [accountData, setAccountData] = useState<UserResponse[]>([]);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [companyFilter, setCompanyFilter] = useState<string>(searchParams.get("company") || "");
  const [statusFilter, setStatusFilter] = useState<"active" | "deactivated">("active");
  const [reactivateUser, setReactivateUser] = useState<UserResponse | null>(null);
  const [isReactivating, setIsReactivating] = useState(false);
  const companyData = useAppSelector((state) => state.companySlice.companyData);

  const handleClose = () => {
    setOpenModal(false);
    setFormData(defaultFormData);
    setFormAction(EFormAction.CREATE);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
    setFormAction(EFormAction.CREATE);
    setFormData(defaultFormData);
  };

  const handleOpenEditModal = (userData: UserResponse) => {
    setOpenModal(true);
    setFormAction(EFormAction.UPDATE);
    setFormData({
      id: userData.id,
      action: EFormAction.UPDATE,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      companyId: userData.company.id as number,
      roleId: userData.roleId,
      departmentId: userData.departmentId,
      templateId: emailTemplate.addAccount,
      websiteRedirect: userData.websiteRedirect,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await confirm({ title: "Delete User", message: "Are you sure you want to delete this user?", confirmText: "Delete" });
      if (confirmed) {
        setIsLoading(true);
        await handleDeleteAccountApi(id);
        setAccountData(accountData.filter((data) => data.id !== id));
        showSuccess("User deleted successfully");
      }

      setIsLoading(false);
    } catch (err) {
      showError("Error deleting user");
      console.error(err);
    }
  };

  const handleReactivate = async (row: UserResponse, websiteRedirect?: string) => {
    try {
      setIsReactivating(true);
      await reactivateAccount(row.id, websiteRedirect);
      setAccountData((prev) => prev.filter((data) => data.id !== row.id));
      setReactivateUser(null);
      showSuccess(websiteRedirect ? "User reactivated and activation email sent" : "User reactivated successfully");
    } catch (err: any) {
      showError(err?.response?.data?.message || "Error reactivating user");
      console.error(err);
    } finally {
      setIsReactivating(false);
    }
  };

  const handleOpenReactivate = async (row: UserResponse) => {
    // Never activated: a new invite goes out, so the admin first picks where its link opens.
    if (!row.hasPassword) {
      setReactivateUser(row);
      return;
    }
    const confirmed = await confirm({
      title: "Reactivate User",
      message: `Reactivate ${row.email}? They sign in with their existing password, so no email is sent.`,
      confirmText: "Reactivate",
      confirmColor: "success",
    });
    if (confirmed) {
      await handleReactivate(row);
    }
  };

  const handleUpdateUser = async () => {
    try {
      setIsLoading(true);
      const { action, templateId, ...rest } = formData;
      const parsedRoleId = parseInt(rest.roleId as string);
      const payload = {
        ...rest,
        companyId: parseInt(rest.companyId as string),
        departmentId: rest.departmentId === 0 ? null : parseInt(rest.departmentId as string),
        roleId: isNaN(parsedRoleId) ? null : parsedRoleId,
      };

      await handleUpdateAccountApi(payload);

      handleClose();
      setIsLoading(false);
      showSuccess("User updated successfully");
    } catch (err) {
      showError("Error updating user");
      console.error(err);
    }
  };
  const handleCreateUser = async () => {
    try {
      setIsLoading(true);
      const { action, id, ...rest } = formData;
      const parsedRoleId = parseInt(rest.roleId as string);
      const payload = {
        ...rest,
        companyId: parseInt(rest.companyId as string),
        departmentId: rest.departmentId ? null : parseInt(rest.departmentId as string),
        roleId: isNaN(parsedRoleId) ? null : parsedRoleId,
      };

      await handleCreateAccountApi(payload);

      handleClose();
      setIsLoading(false);
      showSuccess("User created successfully");
    } catch (err: any) {
      // The API explains a refusal, e.g. that the email belongs to a deactivated user to reactivate.
      showError(err?.response?.data?.message || "Error creating user");
      console.error(err);
    }
  };

  const handleOnChange = (key: string, value: string) => {
    setFormData({
      ...formData,
      [key]: key === "companyId" ? parseInt(value) : value,
    });
  };

  useEffect(() => {
    // Switching lists quickly must not let the older response overwrite the newer one.
    let ignore = false;
    const fetchAccount = async () => {
      try {
        setIsLoadingData(true);
        const response =
          statusFilter === "deactivated" ? await handleGetDeactivatedAccountsApi() : await handleGetAccountsApi();
        if (ignore) return;
        setAccountData(response);

        setIsLoadingData(false);
      } catch (err) {
        if (ignore) return;
        setIsLoadingData(false);
        console.error(err);
      }
    };
    if (user) {
      fetchAccount();
    }
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoading, isReactivating, statusFilter]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const companies = await handleGetCompanies();
        dispatch(setCompanyData(companies.data.data));
      } catch (err) {
        console.error(err);
      }
    };
    fetchCompanies();
  }, [dispatch]);

  const handleCompanyFilterChange = (value: string) => {
    setCompanyFilter(value);
    if (value) {
      setSearchParams({ company: value });
    } else {
      setSearchParams({});
    }
  };

  const filteredAccounts = accountData.filter((row) => {
    const term = search.toLowerCase();
    const matchesSearch =
      row.firstName.toLowerCase().includes(term) ||
      row.lastName.toLowerCase().includes(term) ||
      row.email.toLowerCase().includes(term) ||
      row.company?.name?.toLowerCase().includes(term);
    const matchesCompany = !companyFilter || row.company?.id?.toString() === companyFilter;
    return matchesSearch && matchesCompany;
  });

  const { sortedData: sortedAccounts, sortConfig, requestSort } = useSortable(filteredAccounts, "id", "desc");

  const renderTableSection = () => (
    <>
    <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3 }}>
      <Box sx={{ flex: 1, "& .MuiTextField-root": { mb: 0 } }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search users..." />
      </Box>
      <FormControl sx={{ minWidth: 180 }}>
        <NativeSelect
          value={statusFilter}
          input={<OutlinedInput sx={{ borderRadius: "8px", height: 40 }} />}
          onChange={(e) => setStatusFilter(e.target.value as "active" | "deactivated")}
        >
          <option value="active">Active users</option>
          <option value="deactivated">Deactivated users</option>
        </NativeSelect>
      </FormControl>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <NativeSelect
            value={companyFilter}
            input={<OutlinedInput sx={{ borderRadius: "8px", height: 40 }} />}
            onChange={(e) => handleCompanyFilterChange(e.target.value)}
          >
            <option value="">All Companies</option>
            {companyData.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </NativeSelect>
        </FormControl>
        {companyFilter && (
          <IconButton size="small" onClick={() => handleCompanyFilterChange("")} sx={{ color: "#878787" }}>
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
    <TableContainer sx={{ flex: 1, overflow: "auto" }}>
    <Table aria-label="simple table" stickyHeader>
      <TableHead>
        <TableRow>
          <SortableTableCell label="First name" sortKey="firstName" active={sortConfig?.key === "firstName"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} />
          <SortableTableCell label="Last name" sortKey="lastName" active={sortConfig?.key === "lastName"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} align="center" />
          <SortableTableCell label="Email" sortKey="email" active={sortConfig?.key === "email"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} align="center" />
          <SortableTableCell label="Company" sortKey="company.name" active={sortConfig?.key === "company.name"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} align="center" />
          <TableCell align="center" sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedAccounts.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#878787" }}>
              No users found
            </TableCell>
          </TableRow>
        )}
        {sortedAccounts.map((row, index) => (
          <TableRow
            key={index}
            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
          >
            <TableCell component="th" scope="row">
              {row.firstName}
            </TableCell>
            <TableCell align="center">{row.lastName}</TableCell>
            <TableCell align="center">{row.email}</TableCell>
            <TableCell align="center">{row.company.name}</TableCell>
            <TableCell align="center">
              {statusFilter === "deactivated" ? (
                <Button
                  variant="contained"
                  onClick={() => handleOpenReactivate(row)}
                  disabled={isReactivating}
                  sx={{ ...addButtonSx, height: 32 }}
                >
                  Reactivate
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={() => handleOpenEditModal(row)}
                    sx={editButtonSx}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleDelete(row.id)}
                    sx={deleteButtonSx}
                  >
                    Delete
                  </Button>
                </>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </TableContainer>
    </>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <PageHeader title="Users" subtitle="Manage user accounts and roles" count={accountData.length}>
        <Button variant="contained" onClick={handleOpenModal} sx={addButtonSx}>
          Add account
        </Button>
      </PageHeader>

      {openModal && (
        <UserModal
          openModal={openModal}
          handleClose={handleClose}
          formAction={formAction}
          handleUpdateUser={handleUpdateUser}
          handleCreateUser={handleCreateUser}
          isLoading={isLoading}
          formData={formData}
          handleOnChange={handleOnChange}
        />
      )}

      {reactivateUser && (
        <ReactivateUserModal
          user={reactivateUser}
          isLoading={isReactivating}
          handleClose={() => setReactivateUser(null)}
          handleReactivate={(websiteRedirect) => handleReactivate(reactivateUser, websiteRedirect)}
        />
      )}

      <PageCard>
        {isLoadingData ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderTableSection()
        )}
      </PageCard>
    </Box>
  );
};

export default UserComponent;
